import {Injectable} from '@angular/core';
import {Keys, StorageService} from '../service/storage.service';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {FileDetails} from '../model/file-details.model';
import {LoanApplication} from '../model/loan-application.model';
import {UserAddressInteractor} from '../interactors/user-address.interactor';
import {UserInteractor} from '../interactors/user.interactor';
import {LivelihoodInformationInteractor} from '../interactors/livelihood-information.interactor';
import {FileInteractor} from '../file-upload/file.interactor';
import {AddressProofType} from '../model/address-proof-type.model';
import {BankAccountInteractor} from '../interactors/bank-account.interactor';
import {EmployeeType} from '../model/employee-type.model';
import {UserDocumentInteractor} from '../interactors/user-document.interactor';
import {LoanApplicationDocumentType} from '../model/loan-application-document-type.model';
import {Proof} from '../model/Proof';
import {VerificationStatus} from '../model/user.model';


type UploadDocumentsFormDataType =
    {
        pan: FileDetails[],
        photo: FileDetails[],
        address_proof: FileDetails[],
        salary: FileDetails[],
        itr: FileDetails[],
        other_document: FileDetails[],
        bank: FileDetails[],
        employeeType: EmployeeType,
        addressProofType: AddressProofType,
        panVerificationStatus: VerificationStatus,
        photoVerificationStatus: VerificationStatus
    }
    | null;

@Injectable()
export class UploadDocumentsResolver implements Resolve<UploadDocumentsFormDataType> {
    private uploadDocumentData: UploadDocumentsFormDataType;

    constructor(private storageService: StorageService, private userAddressInteractor: UserAddressInteractor,
                private userInteractor: UserInteractor,
                private livelihoodInformationInteractor: LivelihoodInformationInteractor,
                private fileInteractor: FileInteractor, private bankAccountInteractor: BankAccountInteractor,
                private userDocumentInteractor: UserDocumentInteractor) {
    }

    public async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot):
        Promise<UploadDocumentsFormDataType> {
        this.uploadDocumentData = {
            pan: [],
            photo: [],
            address_proof: [],
            itr: [],
            other_document: [],
            salary: [],
            bank: [],
            employeeType: undefined,
            addressProofType: undefined,
            panVerificationStatus: undefined,
            photoVerificationStatus: undefined
        };

        const user = await this.userInteractor.get();
        const loanApplication = this.storageService.getObject<LoanApplication>(Keys.LOAN_APPLICATION);
        const userAddress = await this.userAddressInteractor.get(loanApplication.addressId);
        const bankAccount = (await this.bankAccountInteractor.get(loanApplication.bankAccountId));
        const livelihoodInformation = await this.livelihoodInformationInteractor.getByLoanApplicationId(loanApplication.id);
        const panDocs = this.getFileIdByProofType(undefined, user.pan.proofs);
        const photoDocs = this.getFileIdByProofType(undefined, user.identity ? user.identity.proofs : undefined);
        const addressDocs = this.getFileIdByProofType(undefined, userAddress.proofs);
        const bankStatement = this.getFileIdByProofType(undefined, bankAccount.proofs);

        const salarySlips = this.getFileIdByProofType(LoanApplicationDocumentType.SALARY_SLIP, livelihoodInformation.proofs);
        const itrStatements = this.getFileIdByProofType(LoanApplicationDocumentType.ITR, (livelihoodInformation.proofs));
        const otherDocuments = this.getFileIdByProofType(LoanApplicationDocumentType.OTHER_DOCUMENT, (livelihoodInformation.proofs));
        const employeeType = livelihoodInformation.employeeType;

        this.uploadDocumentData.addressProofType = userAddress.proofs ? userAddress.proofs[0].proofType as AddressProofType : undefined;
        this.uploadDocumentData.employeeType = employeeType;
        this.uploadDocumentData.panVerificationStatus = user.pan.verificationStatus;
        this.uploadDocumentData.photoVerificationStatus = user.identity.verificationStatus;

        await Promise.all([this.fetchFiles(panDocs, LoanApplicationDocumentType.PAN),
            this.fetchFiles(photoDocs, LoanApplicationDocumentType.PHOTO),
            this.fetchFiles(bankStatement, LoanApplicationDocumentType.BANK_STATEMENT),
            this.fetchFiles(addressDocs, LoanApplicationDocumentType.ADDRESS_PROOF),
            this.fetchFiles(salarySlips, LoanApplicationDocumentType.SALARY_SLIP),
            this.fetchFiles(itrStatements, LoanApplicationDocumentType.ITR),
            this.fetchFiles(otherDocuments, LoanApplicationDocumentType.OTHER_DOCUMENT)]);
        return this.uploadDocumentData;
    }

    private async fetchFiles(docs: string[], type: string): Promise<void> {
        if (!docs || docs.length === 0) {
            return;
        }
        for (let i = 0; i < docs.length; i++) {
            const fileDetails = await this.fileInteractor.getMetadata(docs[i]);
            this.uploadDocumentData[type].push(fileDetails);
        }
    }

    private getFileIdByProofType(documentType: LoanApplicationDocumentType, proofs: Proof[]) {
        if (!proofs || proofs.length === 0) {
            return;
        }
        const fileIds = [];
        proofs.forEach(p => {
            if ((!documentType || (documentType && p.proofType === documentType)) && p.fileIds && p.fileIds.length > 0) {
                fileIds.push(...p.fileIds);
            }
        });
        return fileIds.length > 0 ? fileIds : undefined;
    }
}
